import { publicTtsVote, type PublicTtsVote, type TtsVoteInput } from "@/lib/tts-arena-vote";
import { responseError } from "@/lib/response-error";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Headphones } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArenaPair } from "./tts-arena/ArenaPair";
import { ArenaFilters } from "./tts-arena/ArenaFilters";
import { ArenaLeaderboard } from "./tts-arena/ArenaLeaderboard";
import { loadManifest } from "./tts-arena/data/loadManifest";
import { buildRankings } from "./tts-arena/data/ranking";
import { applyFilters, buildCoverageRows, samplePair } from "./tts-arena/data/sampler";
import type {
  ArenaFilters as ArenaFiltersType,
  ArenaPair as ArenaPairType,
  ArenaSample,
  ArenaVote,
  RankingLevel,
  VoteResult,
} from "./tts-arena/data/types";
import "./tts-arena/TtsArena.css";

const defaultFilters: ArenaFiltersType = {
  language: "all",
  category: "all",
  provider: "all",
};

function ddbVoteToArenaVote(v: PublicTtsVote): ArenaVote {
  return {
    id: v.PK,
    leftSampleId: v.leftSampleId,
    rightSampleId: v.rightSampleId,
    result: v.result,
    filters: {
      language: v.language,
      category: v.category,
    },
    timestamp: v.createdAt,
  };
}

// One sitting's worth of votes share a session id (attribution only).
const SESSION_ID = crypto.randomUUID();

export function TtsArena() {
  const [samples, setSamples] = useState<ArenaSample[]>([]);
  const [votes, setVotes] = useState<ArenaVote[]>([]);
  const [filters, setFilters] = useState<ArenaFiltersType>(defaultFilters);
  const [pair, setPair] = useState<ArenaPairType | null>(null);
  const [previousPairKey, setPreviousPairKey] = useState<string | undefined>();
  const [listened, setListened] = useState({ left: false, right: false });
  const [activeSide, setActiveSide] = useState<"left" | "right" | null>(null);
  const [lastReveal, setLastReveal] = useState<{ left: ArenaSample; right: ArenaSample; result: VoteResult } | null>(null);
  const [savingVote, setSavingVote] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [votesError, setVotesError] = useState<string | null>(null);
  const [votesLoaded, setVotesLoaded] = useState(false);
  const [votesLoading, setVotesLoading] = useState(false);
  const [voteError, setVoteError] = useState<string | null>(null);
  const comparisonId = useRef(crypto.randomUUID());
  const pendingVote = useRef<{ body: TtsVoteInput; pair: ArenaPairType } | null>(null);
  const saving = useRef(false);
  const loadingVotes = useRef(false);
  const [rankingTab, setRankingTab] = useState<RankingLevel>("providerModel");
  const [activeTab, setActiveTab] = useState("arena");
  const [autoAdvanceCountdown, setAutoAdvanceCountdown] = useState<number | null>(null);

  const loadSamples = useCallback(async () => {
    try {
      setSamples(await loadManifest());
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }, []);

  const loadVotes = useCallback(async () => {
    if (loadingVotes.current) return;
    loadingVotes.current = true;
    setVotesLoading(true);
    try {
      const response = await fetch("/api/internal/tts-arena/votes", {
        cache: "no-store", signal: AbortSignal.timeout(10_000),
      });
      if (!response.ok) throw await responseError(response, "Could not load evaluation votes");
      const stored = publicTtsVote.array().parse(await response.json()).map(ddbVoteToArenaVote);
      setVotes((current) => mergeVotes(stored, current));
      setVotesLoaded(true);
      setVotesError(null);
    } catch (cause) {
      setVotesError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      loadingVotes.current = false;
      setVotesLoading(false);
    }
  }, []);

  useEffect(() => { void loadSamples(); void loadVotes(); }, [loadSamples, loadVotes]);

  useEffect(() => {
    setPair(samplePair(samples, votes, filters, previousPairKey));
    comparisonId.current = crypto.randomUUID();
    pendingVote.current = null;
    setVoteError(null);
    setLastReveal(null);
    setListened({ left: false, right: false });
    setActiveSide(null);
  }, [samples, filters]);

  const filteredSamples = useMemo(() => applyFilters(samples, filters), [samples, filters]);
  const providerRankings = useMemo(() => buildRankings(filteredSamples, votes, "providerModel"), [filteredSamples, votes]);
  const voiceRankings = useMemo(() => buildRankings(filteredSamples, votes, "voice"), [filteredSamples, votes]);
  const languageRankings = useMemo(() => buildRankings(filteredSamples, votes, "voiceLanguage"), [filteredSamples, votes]);
  const coverageRows = useMemo(() => buildCoverageRows(samples, votes), [samples, votes]);
  const activeRanking = rankingTab === "providerModel" ? providerRankings : rankingTab === "voice" ? voiceRankings : languageRankings;

  function moveNext(nextVotes = votes) {
    if (saving.current) return;
    pendingVote.current = null;
    comparisonId.current = crypto.randomUUID();
    setVoteError(null);
    const next = samplePair(samples, nextVotes, filters, pair?.pairKey);
    setPreviousPairKey(pair?.pairKey);
    setPair(next);
    setListened({ left: false, right: false });
    setActiveSide(null);
    setLastReveal(null);
    setAutoAdvanceCountdown(null);
  }

  const voted = !!(lastReveal && pair && lastReveal.left.id === pair.left.id);
  const canVote = listened.left && listened.right && !savingVote && !voted && !voteError;

  // Auto-advance countdown after voting
  useEffect(() => {
    if (!voted) return;
    setAutoAdvanceCountdown(5);
    const interval = setInterval(() => {
      setAutoAdvanceCountdown((c) => {
        if (c === null || c <= 1) {
          clearInterval(interval);
          return null;
        }
        return c - 1;
      });
    }, 1000);
    const timeout = setTimeout(() => moveNext(), 5000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [voted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Arrow key shortcuts
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (activeTab !== "arena") return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "ArrowLeft" && canVote && !voted) handleVote("left");
      if (e.key === "ArrowRight" && canVote && !voted) handleVote("right");
      if (e.key === "Enter" && voted) moveNext();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeTab, canVote, voted, pair]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleVote(result: VoteResult) {
    if (!pair || saving.current || voted) return;
    if (result === "skip") { moveNext(); return; }
    if (pendingVote.current) return;
    if (!canVote) return;
    const body: TtsVoteInput = {
      id: comparisonId.current,
      promptId: pair.left.promptId,
      leftSampleId: pair.left.id,
      rightSampleId: pair.right.id,
      leftProvider: pair.left.provider,
      leftModel: pair.left.model,
      rightProvider: pair.right.provider,
      rightModel: pair.right.model,
      result,
      language: pair.left.language,
      category: pair.left.category,
      sessionId: SESSION_ID,
    };
    pendingVote.current = { body, pair };
    await savePendingVote();
  }

  async function savePendingVote() {
    const pending = pendingVote.current;
    if (!pending || saving.current) return;
    saving.current = true;
    setSavingVote(true);
    setVoteError(null);
    try {
      const response = await fetch("/api/internal/tts-arena/votes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(pending.body),
        signal: AbortSignal.timeout(10_000),
      });
      if (!response.ok) throw await responseError(response, "Could not confirm vote");
      const saved = publicTtsVote.parse((await response.json()).vote);
      if (saved.PK !== `VOTE#${pending.body.id}` || saved.result !== pending.body.result ||
        saved.leftSampleId !== pending.body.leftSampleId || saved.rightSampleId !== pending.body.rightSampleId) {
        throw new Error("Unexpected vote confirmation");
      }
      setVotes((current) => mergeVotes(current, [ddbVoteToArenaVote(saved)]));
      setLastReveal({ left: pending.pair.left, right: pending.pair.right, result: saved.result });
    } catch (cause) {
      setVoteError(`Could not confirm your vote. ${cause instanceof Error ? cause.message : String(cause)} Retry the same choice or skip this pair.`);
    } finally {
      saving.current = false;
      setSavingVote(false);
    }
  }

  function changeFilters(next: ArenaFiltersType) {
    if (saving.current) return;
    pendingVote.current = null;
    setFilters(next);
  }

  return (
    <div className="flex flex-col gap-4 min-h-0">
      {/* Stats bar */}
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <Badge variant="secondary">{samples.length} samples</Badge>
        <Badge variant="secondary">{votesLoaded ? `${votes.length} votes${votesError ? " (stale)" : ""}` : votesLoading ? "Loading votes…" : "Vote history unavailable"}</Badge>
        {filteredSamples.length !== samples.length && (
          <Badge variant="outline">{filteredSamples.length} after filters</Badge>
        )}
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
          {samples.length === 0 && <Button className="ml-3" size="sm" variant="outline" onClick={() => void loadSamples()}>Retry loading</Button>}
        </div>
      )}

      {votesError && (
        <div role="alert" className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm">
          {votesLoaded ? "Rankings and coverage use previously loaded votes. " : "Rankings and coverage are unavailable. "}{votesError} You can still listen and vote.
          <Button className="ml-3" size="sm" variant="outline" disabled={votesLoading} onClick={() => void loadVotes()}>{votesLoading ? "Loading votes…" : "Retry votes"}</Button>
        </div>
      )}
      {voteError && (
        <div role="alert" className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {voteError}
          <Button className="ml-3" size="sm" variant="outline" disabled={savingVote} onClick={() => void savePendingVote()}>Retry vote</Button>
          <Button className="ml-3" size="sm" variant="outline" disabled={savingVote} onClick={() => moveNext()}>Skip pair</Button>
        </div>
      )}
      {savingVote && <p role="status" className="text-sm text-muted-foreground">Saving vote…</p>}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="h-9">
          <TabsTrigger value="arena" className="text-xs">Arena</TabsTrigger>
          <TabsTrigger value="leaderboard" className="text-xs">Leaderboard</TabsTrigger>
        </TabsList>
      </Tabs>

      {activeTab === "arena" && (
        <div className="flex flex-col gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex flex-col gap-3">
                <div className="flex items-start gap-3 flex-wrap justify-between">
                  <div>
                    <h2 className="text-sm font-semibold text-foreground">Blind TTS Evaluation</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Two generated clips, same prompt. Play both and pick the one that sounds more natural. Identity revealed after you vote.</p>
                  </div>
                  <div className="flex items-center gap-1.5 rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
                    <Headphones size={13} className="shrink-0" />
                    <span><strong className="text-foreground">Listen gate:</strong> Play 3s of each clip before voting.</span>
                  </div>
                </div>
                {/* Arena filters: language, category only — no provider (defeats blind purpose) */}
                <ArenaFilters samples={samples} filters={filters} onChange={changeFilters} disabled={savingVote} />
              </div>
            </CardContent>
          </Card>

          <ArenaPair
            pair={pair}
            busy={savingVote}
            canVote={canVote}
            voted={voted}
            autoAdvanceCountdown={autoAdvanceCountdown}
            listened={listened}
            activeSide={activeSide}
            onPlay={setActiveSide}
            onListened={(side) => setListened((c) => ({ ...c, [side]: true }))}
            onVote={handleVote}
            onNext={() => moveNext()}
          />

          <AnimatePresence mode="wait">
            {lastReveal && (
              <motion.div
                key={`${lastReveal.left.id}-${lastReveal.right.id}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                <RevealPanel reveal={lastReveal} />
              </motion.div>
            )}
          </AnimatePresence>

          <ShortcutPanel />
        </div>
      )}

      {activeTab === "leaderboard" && !votesLoaded && (
        <p role="status" className="text-sm text-muted-foreground">{votesLoading ? "Loading vote history…" : "Rankings and coverage require complete vote history. Retry votes above."}</p>
      )}
      {activeTab === "leaderboard" && votesLoaded && (
        <div className="flex flex-col gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <ArenaFilters samples={samples} filters={filters} onChange={changeFilters} disabled={savingVote} />
            </CardContent>
          </Card>

          <ArenaLeaderboard
            title={rankingTab === "providerModel" ? "Provider Models" : rankingTab === "voice" ? "Voices" : "Voices by Language"}
            rows={activeRanking}
            showVoice={rankingTab !== "providerModel"}
            showLanguage={rankingTab === "voiceLanguage"}
          />

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Coverage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="tts-coverage-grid">
                {coverageRows.map((row) => (
                  <div className="tts-coverage-cell" key={`${row.language}-${row.provider}`}>
                    <span className="tts-coverage-label">{row.language} · {row.provider}</span>
                    <span className="tts-coverage-count">{row.comparisons}</span>
                    <span className="tts-coverage-sub">{row.samples} samples</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
}

function ShortcutPanel() {
  return (
    <div className="tts-shortcut-panel">
      <span className="tts-shortcut-title">Shortcuts</span>
      <span className="tts-shortcut-item"><kbd>←</kbd> Prefer A</span>
      <span className="tts-shortcut-item"><kbd>→</kbd> Prefer B</span>
      <span className="tts-shortcut-item"><kbd>Enter</kbd> Next pair</span>
    </div>
  );
}

function RevealPanel({ reveal }: { reveal: { left: ArenaSample; right: ArenaSample; result: VoteResult } }) {
  const resultLabel = reveal.result === "left" ? "You preferred Option A"
    : reveal.result === "right" ? "You preferred Option B"
    : "Skipped";

  return (
    <div className="tts-reveal-panel">
      <div className="tts-reveal-header">
        <div className="tts-reveal-eyebrow">Last result</div>
        <div className="tts-reveal-result">{resultLabel}</div>
      </div>
      <div className="tts-reveal-grid">
        <RevealCard label="Option A" sample={reveal.left} isWinner={reveal.result === "left"} />
        <RevealCard label="Option B" sample={reveal.right} isWinner={reveal.result === "right"} />
      </div>
    </div>
  );
}

function RevealCard({ label, sample, isWinner }: { label: string; sample: ArenaSample; isWinner: boolean }) {
  return (
    <div className={`tts-reveal-card ${isWinner ? "tts-reveal-card--winner" : "tts-reveal-card--loser"}`}>
      <div className="tts-reveal-card-label">
        {label}
        {isWinner && (
          <span className="tts-reveal-winner-badge">
            <CheckCircle2 size={10} />
            Winner
          </span>
        )}
      </div>
      <div className="tts-reveal-provider">{sample.provider} · {sample.voiceName}</div>
      <div className="tts-reveal-model">{sample.model}</div>
    </div>
  );
}

function mergeVotes(first: ArenaVote[], second: ArenaVote[]): ArenaVote[] {
  return [...new Map([...first, ...second].map((vote) => [vote.id, vote])).values()]
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp) || a.id.localeCompare(b.id));
}
